import api from "./apiClient";

/* ===================================================== */
/* 🔧 ERROR NORMALIZER                                   */
/* ===================================================== */

function normalizeError(err, fallbackMessage) {
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      fallbackMessage
  );
}

/* ===================================================== */
/* GET ALL PROJECTS                                      */
/* GET /projects                                         */
/* ===================================================== */

export const getMyProjects = async () => {
  try {
    const { data } = await api.get("/projects");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch projects");
  }
};

/* ===================================================== */
/* GET SINGLE PROJECT                                    */
/* GET /projects/:id                                     */
/* ===================================================== */

export const getProject = async (projectId) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  try {
    const { data } = await api.get(`/projects/${projectId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to fetch project");
  }
};

/* ===================================================== */
/* CREATE PROJECT                                        */
/* POST /projects                                        */
/* ===================================================== */

export const createProject = async (payload) => {
  if (!payload?.name?.trim()) {
    throw new Error("Project name is required");
  }

  try {
    const { data } = await api.post("/projects", {
      ...payload,
      name: payload.name.trim(),
    });

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to create project");
  }
};

/* ===================================================== */
/* UPDATE PROJECT                                        */
/* PUT /projects/:id                                     */
/* ===================================================== */

export const updateProject = async (projectId, payload) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  try {
    const { data } = await api.put(`/projects/${projectId}`, payload);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to update project");
  }
};

/* ===================================================== */
/* DELETE PROJECT                                        */
/* DELETE /projects/:id                                  */
/* ===================================================== */

export const deleteProject = async (projectId) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  try {
    const { data } = await api.delete(`/projects/${projectId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to delete project");
  }
};

/* ===================================================== */
/* ADD PROJECT PHASE                                     */
/* POST /projects/:projectId/phases                      */
/* ===================================================== */

export const addProjectPhase = async (projectId, payload) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  if (!payload?.name?.trim()) {
    throw new Error("Phase name is required");
  }

  try {
    const { data } = await api.post(
      `/projects/${projectId}/phases`,
      {
        ...payload,
        name: payload.name.trim(),
      }
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to add project phase");
  }
};

/* ===================================================== */
/* GET PHASE TASKS                                       */
/* GET /projects/:projectId/phases/:phaseId/tasks        */
/* ===================================================== */

export const getPhaseTasks = async (projectId, phaseId) => {
  if (!projectId || !phaseId) {
    throw new Error("projectId and phaseId are required");
  }

  try {
    const { data } = await api.get(
      `/projects/${projectId}/phases/${phaseId}/tasks`
    );

    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch phase tasks");
  }
};

/* ===================================================== */
/* ADD TASK TO PHASE                                     */
/* POST /projects/:projectId/phases/:phaseId/tasks       */
/* ===================================================== */

export const addTaskToPhase = async (
  projectId,
  phaseId,
  payload
) => {
  if (!projectId || !phaseId) {
    throw new Error("projectId and phaseId are required");
  }

  if (!payload?.title?.trim()) {
    throw new Error("Task title is required");
  }

  try {
    const { data } = await api.post(
      `/projects/${projectId}/phases/${phaseId}/tasks`,
      {
        ...payload,
        title: payload.title.trim(),
      }
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to add task");
  }
};

/* ===================================================== */
/* GET PROJECT PROGRESS                                  */
/* GET /projects/:projectId/progress                     */
/* ===================================================== */

export const getProjectProgress = async (projectId) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  try {
    const { data } = await api.get(
      `/projects/${projectId}/progress`
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to fetch project progress");
  }
};

/* ===================================================== */
/* EXPORT                                                */
/* ===================================================== */

const projectService = {
  getMyProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  addProjectPhase,
  getPhaseTasks,
  addTaskToPhase,
  getProjectProgress,
};

export default projectService;